import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  consumeAuthReturnTo,
  resolveAuthReturnTo,
} from '../common/authRedirect';
import { dashboardPath } from '../dashboard/dashboardUrl';
import { useAuth } from './AuthContext';
import LoginLandingPage from './LoginLandingPage';
import { Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';

const AuthComponent = () => {
  const {
    isLoggedIn,
    isGuest,
    authChecked,
    logout,
    user,
  } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fromQuery = searchParams.get('from');
  const redirectedFrom = resolveAuthReturnTo(fromQuery);

  const navigateAfterAuth = () => {
    const returnTo =
      fromQuery && fromQuery !== '/' ? fromQuery : consumeAuthReturnTo();
    navigate(returnTo || dashboardPath(), { replace: true });
  };

  useEffect(() => {
    if (authChecked && isLoggedIn && !isGuest) {
      navigateAfterAuth();
    }
  }, [authChecked, isLoggedIn, isGuest, fromQuery, navigate]);

  const redirectAfterAuth = () => {
    navigateAfterAuth();
  };

  if (!authChecked) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isLoggedIn && !isGuest) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          px: 2,
          py: { xs: 4, md: 6 },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 420,
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          {user && !user.emailVerified && user.provider === 'local' && (
            <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
              Check your inbox and junk folder for a 6-digit code.
            </Typography>
          )}
          <Typography variant="h5" gutterBottom>
            You&apos;re signed in
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {user?.email}
          </Typography>
          <Stack direction="row" spacing={1.5} justifyContent="center">
            <Button variant="contained" onClick={navigateAfterAuth}>
              Continue
            </Button>
            <Button variant="outlined" color="secondary" onClick={() => void logout()}>
              Sign out
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <LoginLandingPage
      redirectedFrom={redirectedFrom}
      onAuthenticated={redirectAfterAuth}
    />
  );
};

export default AuthComponent;
